import { forwardRef, useRef, useState, type ButtonHTMLAttributes, type MouseEvent, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Icon, type IconName } from './Icon';

/**
 * A click handler may return a promise (an API call). While it is pending the
 * button is disabled and shows it is working, so a second tap can't send the
 * same request again and the person can see the first one registered. The ref
 * catches a double click that lands before React re-renders.
 */
type ClickHandler = (e: MouseEvent<HTMLButtonElement>) => unknown;

function usePendingClick(onClick: ClickHandler | undefined) {
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);
  const handle = (e: MouseEvent<HTMLButtonElement>) => {
    if (inFlight.current) {
      e.preventDefault();
      return;
    }
    const out = onClick?.(e);
    if (out && typeof (out as PromiseLike<unknown>).then === 'function') {
      inFlight.current = true;
      setPending(true);
      const done = () => {
        inFlight.current = false;
        setPending(false);
      };
      (out as PromiseLike<unknown>).then(done, done);
    }
  };
  return { pending, handle: onClick ? handle : undefined };
}

export type Accent = 'pop' | 'zest' | 'volt' | 'sky' | 'sun' | 'danger' | 'paper';

// Poster buttons (docs/DESIGN.md): flat fills, fully round, no hover colours
// invented per accent. `pop`/`paper` are the near-black primary; the field
// accents are the poster colours with black text.
const SOLID: Record<Accent, string> = {
  pop: 'bg-primary text-ground hover:bg-primary/90',
  zest: 'bg-green text-night hover:bg-green/90',
  volt: 'bg-red text-night hover:bg-red/90',
  sky: 'bg-yellow text-night hover:bg-yellow/90',
  sun: 'bg-yellow text-night hover:bg-yellow/90',
  danger: 'bg-danger text-ground hover:bg-danger/90',
  paper: 'bg-primary text-ground hover:bg-primary/90',
};
const TEXT: Record<Accent, string> = {
  pop: 'text-fg',
  zest: 'text-fg',
  volt: 'text-fg',
  sky: 'text-fg',
  sun: 'text-fg',
  danger: 'text-danger',
  paper: 'text-fg',
};

export type ButtonVariant = 'solid' | 'outline' | 'ghost' | 'subtle';
type Size = 'sm' | 'md' | 'lg' | 'xl';

const SIZE: Record<Size, string> = {
  sm: 'h-9 px-4 text-sm gap-1.5',
  md: 'h-11 px-5 text-[15px] gap-2',
  lg: 'h-14 px-7 text-base gap-2.5',
  xl: 'h-16 px-8 text-lg gap-3',
};

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  /** Return the promise of an async action to lock the button until it settles. */
  onClick?: ClickHandler;
  accent?: Accent;
  variant?: ButtonVariant;
  size?: Size;
  icon?: IconName;
  iconRight?: IconName;
  loading?: boolean;
  block?: boolean;
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { accent = 'pop', variant = 'solid', size = 'md', icon, iconRight, loading: loadingProp, block, className, children, disabled, type = 'button', onClick, ...rest },
  ref,
) {
  const { pending, handle } = usePendingClick(onClick);
  const loading = loadingProp || pending;
  const look =
    variant === 'solid'
      ? SOLID[accent]
      : variant === 'outline'
        ? cn('border border-fg/25 bg-transparent hover:bg-raised', TEXT[accent])
        : variant === 'subtle'
          ? cn('bg-raised hover:bg-line', TEXT[accent])
          : cn('bg-transparent hover:bg-raised', TEXT[accent]);
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      onClick={handle}
      className={cn(
        'relative inline-flex select-none items-center justify-center whitespace-nowrap rounded-full font-semibold transition-[background-color,border-color,transform,opacity] duration-150 ease-out',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg enabled:active:scale-[0.96]',
        'disabled:cursor-not-allowed disabled:opacity-45',
        SIZE[size],
        look,
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center" aria-hidden>
          <span className="flex gap-1">
            {[0, 1, 2].map((i) => (
              <span key={i} className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" style={{ animationDelay: `${i * 150}ms` }} />
            ))}
          </span>
        </span>
      )}
      <span className={cn('inline-flex items-center gap-[inherit]', loading && 'invisible')}>
        {icon && <Icon name={icon} size={size === 'sm' ? 16 : 20} />}
        {children}
        {iconRight && <Icon name={iconRight} size={size === 'sm' ? 16 : 20} />}
      </span>
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  /** Return the promise of an async action to lock the button until it settles. */
  onClick?: ClickHandler;
  /** Disabled and pulsing while an action it started is in flight. */
  loading?: boolean;
  icon: IconName;
  label: string;
  tone?: 'glass' | 'ink' | 'danger' | 'active' | 'paper';
  size?: 'sm' | 'md' | 'lg';
}

// `glass` sits on live video: a flat dark scrim, no blur.
const ICON_TONE = {
  glass: 'bg-night/60 text-bone hover:bg-night/75',
  ink: 'bg-raised text-fg hover:bg-line',
  danger: 'bg-danger text-ground hover:bg-danger/90',
  active: 'bg-primary text-ground hover:bg-primary/90',
  paper: 'bg-primary text-ground hover:bg-primary/90',
} as const;
const ICON_SIZE = { sm: 'h-9 w-9', md: 'h-12 w-12', lg: 'h-14 w-14' } as const;

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, tone = 'ink', size = 'md', className, type = 'button', onClick, loading: loadingProp, disabled, ...rest },
  ref,
) {
  const { pending, handle } = usePendingClick(onClick);
  const loading = loadingProp || pending;
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      onClick={handle}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full transition-[background-color,transform] duration-150 ease-out enabled:active:scale-[0.96]',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg disabled:opacity-40',
        ICON_TONE[tone],
        ICON_SIZE[size],
        loading && 'animate-pulse',
        className,
      )}
      {...rest}
    >
      <Icon name={icon} size={size === 'sm' ? 18 : size === 'lg' ? 24 : 22} />
    </button>
  );
});
